// shared/config/formModules.js
// Zentrale Registry aller Form-Module
// Wird von Frontend UND Backend importiert

/**
 * Storage-Typ:
 *  - "flat"  → Wert lebt als direkte DB-Spalte (verwaltung_text, einsatzort_text, ...)
 *  - "jsonb" → Wert lebt im `modules` JSONB-Feld
 */

export const MODULE_REGISTRY = {
  verwaltung: {
    id: "verwaltung",
    label: "Verwaltung",
    icon: "Building2",
    storage: "flat",
    flatFields: ["verwaltung_text"],
    fields: [
      {
        name: "verwaltung_text",
        label: "Verwaltung",
        type: "textarea",
        rows: 3,
        placeholder: "z. B.\nLivit AG\nMusterstrasse 1\n8000 Zürich",
        required: false,
      },
    ],
  },

  einsatzort: {
    id: "einsatzort",
    label: "Einsatzort",
    icon: "MapPin",
    storage: "flat",
    flatFields: ["einsatzort_text", "abweichende_adresse"],
    fieldsForProjekt: [
      {
        name: "einsatzort_text",
        label: "Einsatzort",
        type: "textarea",
        rows: 2,
        placeholder: "z. B. Lagerstrasse 3, 8400 Winterthur",
        required: false,
      },
    ],
    fieldsForService: [
      {
        name: "abweichende_adresse",
        label: "Einsatzort",
        type: "textarea",
        rows: 2,
        placeholder: "z. B. Lagerstrasse 3, 8400 Winterthur",
        required: false,
      },
    ],
  },

  kontakt_vor_ort: {
    id: "kontakt_vor_ort",
    label: "Kontakt vor Ort",
    icon: "UserRound",
    storage: "jsonb",
    fields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "z. B. Hans Müller",
        required: false,
      },
      {
        name: "telefon",
        label: "Telefon",
        type: "tel",
        placeholder: "+41 79 123 45 67",
        required: false,
      },
      {
        name: "email",
        label: "E-Mail",
        type: "email",
        placeholder: "hans@example.com",
        required: false,
      },
      {
        name: "funktion",
        label: "Funktion",
        type: "text",
        placeholder: "z. B. Hauswart",
        required: false,
      },
    ],
  },

  rechnungsadresse: {
    id: "rechnungsadresse",
    label: "Rechnungsadresse (abweichend)",
    icon: "Receipt",
    storage: "jsonb",
    fields: [
      {
        name: "firma",
        label: "Firma / Name",
        type: "text",
        placeholder: "Verwaltung AG",
        required: false,
      },
      {
        name: "zusatz",
        label: "Adresszusatz",
        type: "text",
        placeholder: "z. B. z.Hd. Buchhaltung",
        required: false,
      },
      {
        name: "strasse",
        label: "Strasse",
        type: "text",
        placeholder: "Musterstrasse 1",
        required: false,
      },
      {
        name: "plz",
        label: "PLZ",
        type: "text",
        placeholder: "8000",
        required: false,
        width: "half",
      },
      {
        name: "ort",
        label: "Ort",
        type: "text",
        placeholder: "Zürich",
        required: false,
        width: "half",
      },
    ],
  },

  interne_referenz: {
    id: "interne_referenz",
    label: "Interne Referenz",
    icon: "Hash",
    storage: "jsonb",
    fields: [
      {
        name: "referenz_nr",
        label: "Referenz-Nr.",
        type: "text",
        placeholder: "z. B. REF-2026-001",
        required: false,
      },
      {
        name: "kostenstelle",
        label: "Kostenstelle",
        type: "text",
        placeholder: "z. B. KST-400",
        required: false,
      },
      {
        name: "bemerkung",
        label: "Bemerkung",
        type: "textarea",
        rows: 2,
        placeholder: "Interne Notiz...",
        required: false,
      },
    ],
  },

  zusatzinfos: {
    id: "zusatzinfos",
    label: "Zusatzinfos",
    icon: "Info",
    storage: "jsonb",
    fields: [
      {
        name: "text",
        label: "Zusätzliche Informationen",
        type: "textarea",
        rows: 3,
        placeholder: "Freitext...",
        required: false,
      },
      {
        name: "intern_notiz",
        label: "Interne Notiz (nicht auf Dokumenten)",
        type: "textarea",
        rows: 2,
        placeholder: "Nur intern sichtbar...",
        required: false,
      },
    ],
  },
};

/**
 * Welche Module sind verfügbar und standardmässig aktiv — pro Entity-Typ.
 *
 * available: Module die im Toggle erscheinen
 * defaultEnabled: Module die bei neuem Eintrag schon aktiv sind (Toggle + Blöcke).
 * Projekte: wie Service leer lassen — Zusatzbereiche nur bei Bedarf / bei vorhandenen Daten.
 */
export const MODULE_DEFAULTS = {
  // Projekte
  elektro: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
  pv: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
  sicherheit: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
  ladestation: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
  ladenbau: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
  intern: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },

  // Serviceaufträge
  service: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },

  // Mängelberichte (gleiche Module wie Service, da bidirektional gespiegelt)
  maengel: {
    available: [
      "verwaltung",
      "einsatzort",
      "kontakt_vor_ort",
      "rechnungsadresse",
      "interne_referenz",
      "zusatzinfos",
    ],
    defaultEnabled: [],
  },
};

function valueMeaningful(v) {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.some(valueMeaningful);
  if (typeof v === "object") return Object.values(v).some(valueMeaningful);
  return false;
}

/** True, wenn für dieses Modul bereits Formular-/DB-Inhalt existiert (flach oder JSONB). */
export function moduleHasStoredContent(form, moduleId) {
  const mod = MODULE_REGISTRY[moduleId];
  if (!mod || !form || typeof form !== "object") return false;
  if (mod.storage === "flat") {
    for (const f of mod.flatFields || []) {
      if (valueMeaningful(form[f])) return true;
    }
    return false;
  }
  return valueMeaningful(form.modules?.[moduleId]);
}

/** Modul-IDs mit Inhalt (z. B. Auto-Aktivierung in der Form). */
export function listModuleIdsWithContent(form, availableIds) {
  if (!Array.isArray(availableIds)) return [];
  return availableIds.filter((id) => moduleHasStoredContent(form, id));
}

/** Alle registrierten Modul-IDs (Whitelist) */
export const ALL_MODULE_IDS = Object.keys(MODULE_REGISTRY);

/** Gibt die Felder eines Moduls zurück, ggf. entity-spezifisch */
export function getModuleFields(moduleId, entityType = "projekt") {
  const mod = MODULE_REGISTRY[moduleId];
  if (!mod) return [];

  if ((entityType === "service" || entityType === "maengel") && mod.fieldsForService) {
    return mod.fieldsForService;
  }
  if (entityType === "projekt" && mod.fieldsForProjekt) {
    return mod.fieldsForProjekt;
  }
  return mod.fields || [];
}

/** Gibt Defaults für einen Entity-Typ zurück */
export function getDefaults(entityType) {
  return MODULE_DEFAULTS[entityType] || MODULE_DEFAULTS.elektro;
}
